import { TranslocoService } from '@jsverse/transloco';
import { Injectable, OnDestroy } from '@angular/core';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { MatDialog } from '@angular/material/dialog';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { Observable } from 'rxjs';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { CreditsService } from 'src/app/services/credits-service';
import {
  ConfirmAnalysisDialogComponent,
  ConfirmAnalysisDialogData,
  ConfirmAnalysisDialogMode,
} from 'src/app/components/dialogs/confirm-analysis-dialog/confirm-analysis-dialog.component';
import { DialogActions } from 'src/app/model/dictionary';
import { NgxSpinnerService } from 'ngx-spinner';

export enum AnalysisType {
  MasterData,
  Recyclability,
  Lifecycle,
  PlasticTax,
  LicenseFee,
  CradleToGateLifecycle,
  ExpenseCalculation,
}

@Injectable({ providedIn: 'root' })
export class ParentNavigationService implements OnDestroy {
  protected readonly CREDITS_THRESHOLD_FOR_DIALOG = 50;
  protected dialogSubscription?: Subscription;
  protected evaluationSubscription?: Subscription;

  constructor(
    protected dialog: MatDialog,
    protected translateService: TranslocoService,
    protected analysisApiService: AnalysisApiService,
    protected creditsService: CreditsService
  ) {}

  protected getAvailableCreditsForAnalysis(analysisType: AnalysisType): number {
    switch (analysisType) {
      case AnalysisType.Recyclability:
        return this.creditsService.creditsCount.recyclabilityCredits;
      case AnalysisType.Lifecycle:
      case AnalysisType.CradleToGateLifecycle:
        return this.creditsService.creditsCount.lcaCredits;
      case AnalysisType.ExpenseCalculation:
        return this.creditsService.creditsCount.recyclabilityCredits;
      default:
        return 0;
    }
  }

  protected getAnalysisCost(analysisType: AnalysisType, packagingPart: PackagingPart, packagingSystemId: number, versionId: number): Observable<number> {
    switch (analysisType) {
      case AnalysisType.Recyclability:
        return this.analysisApiService.getRecyclabilityCreditsRequirement(packagingSystemId, packagingPart, versionId);
      case AnalysisType.Lifecycle:
        return this.analysisApiService.getLifeCycleCreditsRequirement(packagingSystemId, packagingPart, versionId);
      case AnalysisType.CradleToGateLifecycle:
        return this.analysisApiService.getLifeCycleCreditsRequirement(packagingSystemId, packagingPart, versionId);
      case AnalysisType.ExpenseCalculation:
        // is this correct?
        return this.analysisApiService.getExpenseCreditsRequirement(packagingSystemId, packagingPart, versionId);
      default:
        return new Observable<number>(subscriber => {
          subscriber.next(0);
          subscriber.complete();
        });
    }
  }

  protected getAnalysisTypeLabel(analysisType: AnalysisType): string {
    switch (analysisType) {
      case AnalysisType.Recyclability:
        return this.translateService.translate('analysis.recyclabilityAnalysis.title');
      case AnalysisType.Lifecycle:
        return this.translateService.translate('analysis.lifecycleAnalysis.title');
      case AnalysisType.CradleToGateLifecycle:
        return this.translateService.translate('analysis.lifecycleAnalysis.lifecycleTypes.cradleToGateShort');
      case AnalysisType.ExpenseCalculation:
        return this.translateService.translate('analysis.expenseCalculation.title');
      default:
        return '';
    }
  }

  protected showAnalysisDialog(
    analysisType: AnalysisType,
    cost: number,
    availableCredits: number,
    mode: ConfirmAnalysisDialogMode,
    spinner: NgxSpinnerService,
    onConfirm: () => void
  ) {
    const dialogData: ConfirmAnalysisDialogData = {
      analysisTypeLabel: this.getAnalysisTypeLabel(analysisType),
      cost: cost,
      availableCredits: availableCredits,
      mode: mode,
    };

    const dialogConfig = getDialogConfig(dialogData);
    const dialogRef = this.dialog.open(ConfirmAnalysisDialogComponent, dialogConfig);

    spinner.hide();

    dialogRef.afterClosed().subscribe(result => {
      if (result?.event === DialogActions.CONFIRM) {
        spinner.show();
        onConfirm();
      }
    });
  }

  stopWhenAnalysisNotPermitted(): void {
    const data: SimpleDialogData = {
      title: this.translateService.translate('common.text.information'),
      messages: [this.translateService.translate('warnings.analysisNotPermitted')],
      icon: 'info',
    };
    this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(data));
  }

  ngOnDestroy(): void {
    this.dialogSubscription?.unsubscribe();
    this.evaluationSubscription?.unsubscribe();
  }
}
